import { useMemo } from "react";

import type { CampaignKey } from "@/campaigns/campaign-key";
import type { CampaignSchema } from "@/types";
import type { RevealedPrize } from "@/stores/notifications.store";
import { campaignKey } from "@/campaigns/campaign-key";
import {
  claimAllWonPrizes,
  claimHeadWonPrize,
  dismissRevealedPrize,
  retryRevealBatch,
  revealAllRemaining,
  settleRevealedPrize,
} from "@/services/notifications";
import { useAppStore } from "@/stores";

export type RevealStatus = "idle" | "claiming" | "revealed" | "failed";

/** What a confirmed reveal led to, so a surface can follow up:
 *  - `revealing` — the next prize is already being claimed; keep the ceremony open.
 *  - `done` — nothing left; close.
 *
 *  Deliberately only two outcomes. An earlier version routed back to the reveal/reveal-all
 *  chooser when several prizes remained, which cost a tap per prize — the exact friction #3815
 *  exists to remove. "Reveal all" is offered inside the reveal instead, so the choice stays
 *  available without interrupting the run. */
export type RevealNext = "revealing" | "done";

export interface RevealRow extends RevealedPrize {
  dropKey: string;
}

export interface RevealFlow {
  status: RevealStatus;
  /** Prizes committed in this reveal — the snapshot taken when the claim was sent, never the
   *  live queue (which floors at 1 and grows if a new win lands mid-reveal). 0 when idle. */
  count: number;
  /** The disclosed prizes, in the order they were claimed. Empty until the batch settles. */
  rows: RevealRow[];
  claimHead: () => void;
  /** Commit every queued prize for this campaign in one claim (#3815). */
  claimAll: () => void;
  retry: () => void;
  /** Confirm a settled reveal, and say what should happen next (see `RevealNext`). Use this for
   *  a ceremony's confirm button; `dismiss` is for abandoning a failed one. */
  confirm: () => RevealNext;
  /** "Reveal all" from inside a reveal: settle the prize on screen, then claim everything still
   *  queued in one batch, so a player part way through a one-at-a-time run can skip the rest. */
  confirmAll: () => void;
  dismiss: () => void;
}

/**
 * The reveal state machine, shared by both surfaces (the in-widget card and the in-game
 * launcher). Lives in the store rather than component state because:
 *
 *  - `detailView` mounts the reveal card TWICE (mobile + desktop), and the launcher mounts from
 *    a different custom element / shadow root — component state cannot cross either boundary,
 *    but the module-singleton store can;
 *  - a claim is money in flight, so unmounting the surface (the player navigates away) must not
 *    abandon it.
 *
 * `campaign` is optional so callers can invoke this above their own early returns.
 */
export function useRevealFlow(campaign?: CampaignSchema): RevealFlow {
  const key: CampaignKey | undefined = campaign
    ? campaignKey(campaign)
    : undefined;
  const batch = useAppStore((s) =>
    key ? s.revealBatchByCampaign[key] : undefined
  );
  // Subscribed, not read imperatively: a disclosure landing has to re-render the open reveal.
  const disclosedTitles = useAppStore((s) => s.disclosedTitles);

  const rows = useMemo<RevealRow[]>(() => {
    if (!batch) return [];
    return batch.targets
      .flatMap((target) => target.dropKeys)
      .flatMap((dropKey) => {
        const disclosed = disclosedTitles[dropKey];
        return disclosed ? [{ ...disclosed, dropKey }] : [];
      });
  }, [batch, disclosedTitles]);

  return useMemo<RevealFlow>(
    () => ({
      status: batch?.status ?? "idle",
      count: batch?.count ?? 0,
      rows,
      claimHead: () => key && claimHeadWonPrize(key),
      claimAll: () => key && claimAllWonPrizes(key),
      retry: () => key && retryRevealBatch(key),
      confirm: () => {
        if (!key) return "done";
        if (settleRevealedPrize(key) === 0) return "done";
        // Straight into the next prize, whatever the count — one tap per prize is the whole
        // point of "Next Prize", and the reveal window carries its own "Reveal all" for anyone
        // who wants out of the run.
        claimHeadWonPrize(key);
        return "revealing";
      },
      confirmAll: () => key && revealAllRemaining(key),
      dismiss: () => key && dismissRevealedPrize(key),
    }),
    [batch, rows, key]
  );
}
